/* This example requires Tailwind CSS v2.0+ */
import React, { Fragment } from "react";
import { Dialog as ReactDialog, Transition } from "@headlessui/react";
import { Button } from "../Button";
import { useAssessmentMutations } from "../../hooks/useAssessmentMutations";

type ConfirmDeleteDialogProps = {
  assessmentId: string;
  open?: boolean;
  onClose?: () => void;
  archived: boolean;
};

const ConfirmArchiveDialog = ({
  assessmentId,
  open = false,
  onClose,
  archived,
}: ConfirmDeleteDialogProps) => {
  const { updateArchived } = useAssessmentMutations(assessmentId);

  const archivedText = archived ? "Unarchive" : "Archive";

  const cancelButtonRef = React.useRef(null);

  const onSubmit = () => {
    updateArchived(!archived);
    handleClose();
  };

  const handleClose = React.useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <ReactDialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={() => handleClose()}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <ReactDialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left flex flex-col w-full">
                  <ReactDialog.Title
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900"
                  >
                    {archivedText} assessment
                  </ReactDialog.Title>
                  <div className="mt-2 space-y-2 flex flex-col">
                    <div className="text-sm text-gray-500">
                      Are you sure you want to {archivedText} this assessment?
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <Button
                  as="button"
                  type="button"
                  className="ml-2"
                  color="secondary"
                  onClick={() => onSubmit()}
                >
                  {archivedText}
                </Button>
                <Button
                  as="button"
                  type="button"
                  onClick={() => handleClose()}
                  variant="outlined"
                  ref={cancelButtonRef}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </ReactDialog>
    </Transition.Root>
  );
};

export { ConfirmArchiveDialog };
